import { Component } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { ApiService } from 'src/app/api.service'
import { ContributorView, InvoiceView, PaymentOrderView } from 'src/app/models/invoicer_query.models'
import { CitySummary, Item } from 'src/app/models/models'
import { CachedContributorView, OfflineDatabase } from 'src/app/offline_db'
import { State } from 'src/app/state'
import { error_toast, success_toast } from 'src/app/utils/toast.util'
import { is_nothing, since, zero_pad } from 'src/app/utils/utils'
import { environment } from 'src/environments/environment'
import { invoicer_queriable_to_item } from '../payments.component'

@Component({
	selector: 'app-billfast',
	templateUrl: './billfast.component.html',
	styleUrls: ['./billfast.component.less'],
})
export class BillfastComponent {
	// Main Search Input (Original)
    filter: string = ''
	
    // Route Search Filters (New)
    search_type: 'monthly' | 'standard' = 'monthly'
    filter_zone: string = null
    filter_day: number = null

    mode = 1
	working = {
		main: false,
		download: false,
	}
	
	result: ContributorView
	result_order: PaymentOrderView
	
	cities: CitySummary[] = []
    
    // Data Management
    cached_contracts: CachedContributorView[] = [] 
	contracts_list: ContributorView[] = [] // List result for Route search

	ayuntamiento: string = ''
	ayuntamientoPath: string = ''
    payment_policy: Item

	get partial_payments(): boolean { return State.active_invoicer.partial_payments }
    get is_online(): boolean { return State.online }

	constructor(private api: ApiService, private router: Router, private active_route: ActivatedRoute) {
		this.ayuntamiento = State.active_invoicer.name.toLowerCase()
		this.ayuntamientoPath = active_route.snapshot.routeConfig.path

		if (State.preloaded_query) {
			this.filter = State.preloaded_query.id
			this.result = State.preloaded_query.data
			State.preloaded_query = null
		}
		this.payment_policy = State.active_invoicer.invoice_payment_policy

		this.load_zones()
		this.load_cached_db() 
	}

	load_zones() {
        // Try to load from local storage first (for offline support)
        const cached_zones = localStorage.getItem(`zones_${this.ayuntamientoPath}`);
        if (cached_zones) {
            this.cities = JSON.parse(cached_zones);
        }

		if (State.online) {
			this.api.get_cities(State.active_invoicer.id, (response) => {
				if (response.succeeded) {
    				this.cities = response.data.sort((a, b) => a.city_name.localeCompare(b.city_name));
                    // Update cache
                    localStorage.setItem(`zones_${this.ayuntamientoPath}`, JSON.stringify(this.cities));
				}
			})
        }
	}

	since(_, __) { return since(_, __) }

	load_cached_db() {
		environment.debug('[i] loading offline contracts...')
		const db_method = (callback: (data: CachedContributorView[]) => void) => {
			OfflineDatabase.get_contracts(this.ayuntamientoPath, callback);
		};

		db_method((data) => {
			if (data.length) {
				const expired_contracts = data.filter((d) => since(d.date, { hours: 12 }))
				
				// Remove expired
				for (let contract of expired_contracts) {
					const delete_method = (contract_number: string, callback?: () => void) => {
						OfflineDatabase.delete_contract(this.ayuntamientoPath, contract_number, callback);
					};
					delete_method(contract.document)
				}
				
				this.cached_contracts = data.filter((d) => !expired_contracts.includes(d))
			}
		})
	}

    // Download Logic: Saves Contracts AND Zones
 	download_current_list() {
        if (!this.contracts_list || this.contracts_list.length === 0) return;

        this.working.download = true;
        let processed_count = 0;
        const total_to_process = this.contracts_list.length;

        // Guardar zonas para uso offline
        if(this.cities.length > 0) {
            localStorage.setItem(`zones_${this.ayuntamientoPath}`, JSON.stringify(this.cities));
        }

        this.contracts_list.forEach((contract) => {
            const db_entry: CachedContributorView = {
                date: new Date(Date.now()), 
                document: contract.document,
                name: contract.name,
                address: contract.address,
                total_debt: contract.total_debt,
                phone: contract.phone,
                pending_invoices: contract.pending_invoices,
                // Agregamos la metadata del filtro actual
                zone_id: this.filter_zone ? parseInt(this.filter_zone) : null,
                day: (this.search_type === 'monthly' && this.filter_day) ? this.filter_day : null
            };

            OfflineDatabase.add_or_update_contract(this.ayuntamientoPath, db_entry, () => {
                processed_count++;
                if (processed_count === total_to_process) {
                    this.working.download = false;
                    this.load_cached_db(); 
                    success_toast(`${total_to_process} contribuyentes guardados exitosamente.`);
                }
            });
        });
    }

    // Unified Query
	query(type: 'direct' | 'route') {
		this.result = null; 
        
        // Only clear the list if we are switching to route search
        if(type === 'route') this.contracts_list = []; 
		
		this.working.main = true

		if (this.mode === 1) {
            if (type === 'direct') {
                // 1. Direct Search Logic
                if (!this.filter || !this.filter.trim()) { this.working.main = false; return; }
                const searchValue = this.filter.trim();
                
                if (State.online) {
                    // Try Online API
                    this.api.query_billfast(this.ayuntamiento, searchValue, (response) => {
                        this.working.main = false
                        if (response.succeeded) {
                            this.result = response.data
                        } else error_toast(response.error.message)
                    })
                } else {
                    // Offline Fallback: Search in local cache
                    this.working.main = false;
                    const found = this.cached_contracts.find(c => c.document === searchValue);
                    
                    if (found) {
                        this.result = found;
                        success_toast("Registro encontrado en base de datos offline");
                    } else {
                        error_toast("Sin conexión. El documento no se encuentra descargado offline.");
                    }
                }

            } else {
                // 2. Route Search Logic
                if(!this.filter_zone) { 
                    error_toast("Debe seleccionar una zona"); 
                    this.working.main = false; 
                    return; 
                }

                if (State.online) {
                    let dayParam = (this.search_type === 'monthly') ? this.filter_day : null;
                    
                    this.api.get_billfast_clients(State.active_invoicer.id, parseInt(this.filter_zone), (response) => {
                        this.working.main = false;
                        if (response.succeeded) {
                            this.contracts_list = response.data;
                            if(this.contracts_list.length === 0) success_toast("No se encontraron resultados en esta ruta.");
                        } else {
                            error_toast(response.error.message);
                        }
                    }, dayParam); 
                } else {
                    this.working.main = false;
                    this.search_offline_cache();
                }
            }
		} else if (this.mode === 2) {
            // Mode 2: Orders (Standard behavior)
			this.api.query_order_billfast(this.ayuntamiento, this.filter.trim(), (response) => {
				this.working.main = false
				if (response.succeeded) {
					this.result_order = response.data
				} else error_toast(response.error.message)
			})
		}
	}

	search_offline_cache() {
        if (!this.filter_zone) return;

        const targetZone = parseInt(this.filter_zone);
        
        this.contracts_list = this.cached_contracts.filter(c => {
            // 1. Validar Zona
            if (c.zone_id && c.zone_id !== targetZone) return false;

            // 2. Validar Día (si es mensual)
            if (this.search_type === 'monthly') {
                if (this.filter_day && c.day !== this.filter_day) return false;
            } 
            // 3. Validar Estándar (excluir los que tienen día asignado si estamos buscando estándar)
            else if (this.search_type === 'standard') {
                if (c.day != null) return false; 
            }

            // Fallback: Si la data es vieja y no tiene zone_id, intentar por address (opcional)
            if (!c.zone_id) {
                const zoneName = this.cities.find(z => z.city_id == this.filter_zone)?.city_name;
                return zoneName && c.address && c.address.toLowerCase().includes(zoneName.toLowerCase());
            }

            return true;
        });

        if(this.contracts_list.length === 0) {
            success_toast("No hay datos offline guardados para estos criterios.");
        }
    }

	select_contract(contract: ContributorView) {
		this.result = contract
	}

    // --- Selectable Logic (Unchanged) ---
	selectable_invoices: SelectableInvoice[] = []
	selectable(invoice: InvoiceView): SelectableInvoice {
		var sel_inv: SelectableInvoice = this.selectable_invoices.find((i) => i.id === invoice.id)
		if (sel_inv) return sel_inv
		else {
			const component: BillfastComponent = this
			let selected = true
			let amount_to_pay = invoice.amount
			sel_inv = {
				id: invoice.id,
				get selected(): boolean { return selected },
				set selected(value: boolean) {
					if (component.payment_policy.id === 2) {
						const issue_date = new Date(invoice.issue_date)
						if (value) {
							component.selectable_invoices
								.filter((si) => new Date(si.invoice.issue_date) < issue_date)
								.reverse()
								.forEach((si) => {
									si.selected = true
									si.amount_to_pay = si.invoice.amount
								})
						} else {
							component.selectable_invoices
								.filter((si) => si !== sel_inv && new Date(si.invoice.issue_date) >= issue_date && si.selected)
								.reverse()
								.forEach((si) => { si.selected = false })
						}
					}
					selected = value
				},
				invoice,
				get amount_to_pay(): number { return amount_to_pay },
				set amount_to_pay(value: number) {
					if (component.payment_policy.id === 2) {
						if (value < invoice.amount) {
							const issue_date = new Date(invoice.issue_date)
							component.selectable_invoices
								.filter((si) => si !== sel_inv && si.selected && new Date(si.invoice.issue_date) >= issue_date)
								.reverse()
								.forEach((si) => { si.selected = false })
						}
					}
					amount_to_pay = value
				},
			}
			this.selectable_invoices.push(sel_inv)
			return sel_inv
		}
	}

	get selected(): SelectableInvoice[] {
		return this.selectable_invoices.filter((s) => s.selected)
	}

	get can_pay(): boolean {
        if (!this.result) return false;
		if(this.result.total_debt > 0 && this.result.pending_invoices.length === 0) return true
		return (
			this.selected.length > 0 &&
			this.selected.every((s) => s.amount_to_pay >= 0 && s.amount_to_pay <= s.invoice.amount)
		)
	}

	pay() {
		switch (this.mode) {
			case 1:
				if (this.selected.length === 0)  {
					State.payment_package = [{
						name: this.result.name,
						document: this.result.document,
						invoicer: invoicer_queriable_to_item(State.active_invoicer),
						type: 1,
						payloads: [{ concept: null, invoice_id: null, amount: this.result.total_debt }]
					}]
				} else {
					State.payment_package = [{
						name: this.result.name,
						document: this.result.document,
						invoicer: invoicer_queriable_to_item(State.active_invoicer),
						type: 1,
						payloads: this.selected.map((s) => ({
							invoice_id: s.id,
							concept: s.invoice.concept,
							amount: s.amount_to_pay,
						})),
					}]
				}
				break
			case 2:
				State.payment_package = [{
					name: this.result_order.name,
					document: this.result_order.document,
					invoicer: invoicer_queriable_to_item(State.active_invoicer),
					type: 3,
					payloads: [{
						invoice_id: this.result_order.id,
						concept: this.result_order.description,
						amount: this.result_order.amount,
					}],
				}]
				break
		}
		this.router.navigate(['cashier', 'apply-payment'])
	}

	get mobile(): boolean { return window.innerWidth < 768 }
}

interface SelectableInvoice {
	id: string
	invoice: InvoiceView
	selected: boolean
	amount_to_pay: number
}